import { FC, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Formik, Form, Field, FieldProps } from 'formik';
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineLock } from 'react-icons/ai';
import { HiOutlineArrowLeft, HiOutlineCheck } from 'react-icons/hi';
import { AuthLayout } from '../../../layouts';
import { changePasswordSchema } from '../../../../constants';
import { DAV_APIS } from '../../../../adapters';
import { Alert } from '../../../commons';

const ChangePassword: FC = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const { token } = useParams<{ token: string }>();

  const handleChangePassword = async (values: any) => {
    setLoading(true);
    try {
      await DAV_APIS.auth.resetPassword(values.password, token as string);
      setLoading(false);
      setPasswordChanged(true);
      Alert({ message: 'Password Changed Successfully', variant: 'success' });
    } catch (error) {
      setLoading(false);
      Alert({ message: 'Failed to change password', variant: 'error' });
    }
  };

  // Success state after password is changed
  if (passwordChanged) {
    return (
      <AuthLayout title='Password Reset' subtitle='Your password has been successfully changed'>
        <div className='text-center space-y-6'>
          {/* Success icon */}
          <div className='mx-auto w-16 h-16 bg-green-100 dark:bg-green-500/20 rounded-full flex items-center justify-center'>
            <HiOutlineCheck className='w-8 h-8 text-green-600 dark:text-green-400' />
          </div>

          <div className='space-y-2'>
            <p className='text-gray-600 dark:text-gray-400'>
              You can now use your new password to sign in to your account.
            </p>
          </div>

          <Link
            to='/'
            className='
              w-full h-12 
              flex items-center justify-center gap-2
              text-white font-semibold
              rounded-xl
              shadow-lg
              transition-all duration-200
              hover:opacity-90
            '
            style={{
              background: 'linear-gradient(to right, #10B981, #059669)',
            }}
          >
            Continue to Login
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout 
      title='Reset Password' 
      subtitle='Create a new password for your account'
    >
      <Formik
        enableReinitialize
        initialValues={{
          password: '',
          confirmPassword: '',
        }}
        validationSchema={changePasswordSchema}
        onSubmit={async (values, { resetForm }) => {
          await handleChangePassword(values);
          resetForm();
        }}
      >
        {() => (
          <Form className='space-y-5'>
            {/* Password requirements hint */}
            <div className='p-3 bg-blue-50 dark:bg-blue-500/10 rounded-xl border border-blue-100 dark:border-blue-500/20'>
              <p className='text-sm text-blue-700 dark:text-blue-400'>
                Password must be at least 8 characters long
              </p>
            </div>

            {/* Password Field */}
            <Field name='password'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <label 
                    htmlFor='password' 
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    New Password
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
                      <AiOutlineLock className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      {...field}
                      id='password'
                      type={showPassword ? 'text' : 'password'}
                      autoComplete='new-password'
                      placeholder='••••••••'
                      className={`
                        w-full h-12 pl-12 pr-12 
                        bg-gray-50 dark:bg-gray-800 
                        border rounded-xl
                        text-gray-900 dark:text-white
                        placeholder:text-gray-400
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                        ${meta.touched && meta.error ?
                          'border-red-500'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}
                      `}
                    />
                    <button
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      className='absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors'
                    >
                      {showPassword ? (
                        <AiOutlineEyeInvisible className='h-5 w-5' />
                      ) : (
                        <AiOutlineEye className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                  {meta.touched && meta.error && (
                    <p className='text-sm text-red-500 flex items-center gap-1'>
                      <span className='inline-block w-1 h-1 rounded-full bg-red-500' />
                      {meta.error}
                    </p>
                  )}
                </div>
              )}
            </Field>

            {/* Confirm Password Field */}
            <Field name='confirmPassword'>
              {({ field, meta }: FieldProps) => (
                <div className='space-y-2'>
                  <label 
                    htmlFor='confirmPassword' 
                    className='block text-sm font-medium text-gray-700 dark:text-gray-300'
                  >
                    Confirm New Password
                  </label>
                  <div className='relative'>
                    <div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
                      <AiOutlineLock className='h-5 w-5 text-gray-400' />
                    </div>
                    <input
                      {...field}
                      id='confirmPassword'
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete='new-password'
                      placeholder='••••••••'
                      className={`
                        w-full h-12 pl-12 pr-12 
                        bg-gray-50 dark:bg-gray-800 
                        border rounded-xl
                        text-gray-900 dark:text-white
                        placeholder:text-gray-400
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500
                        ${meta.touched && meta.error ?
                          'border-red-500'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}
                      `}
                    />
                    <button
                      type='button'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className='absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors'
                    >
                      {showConfirmPassword ? (
                        <AiOutlineEyeInvisible className='h-5 w-5' />
                      ) : (
                        <AiOutlineEye className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                  {meta.touched && meta.error && (
                    <p className='text-sm text-red-500 flex items-center gap-1'>
                      <span className='inline-block w-1 h-1 rounded-full bg-red-500' />
                      {meta.error}
                    </p>
                  )}
                </div>
              )}
            </Field>

            {/* Submit Button */}
            <button
              type='submit'
              disabled={loading}
              style={{
                background: 'linear-gradient(to right, #10B981, #059669)',
              }}
              className='
                w-full h-12 
                text-white font-semibold
                rounded-xl
                shadow-lg
                transition-all duration-200
                hover:opacity-90
                hover:-translate-y-0.5
                active:translate-y-0
                disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0
                flex items-center justify-center gap-2
              '
            >
              {loading ? (
                <>
                  <svg 
                    className='animate-spin h-5 w-5' 
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <circle 
                      className='opacity-25' 
                      cx='12' 
                      cy='12' 
                      r='10' 
                      stroke='currentColor' 
                      strokeWidth='4'
                    />
                    <path 
                      className='opacity-75' 
                      fill='currentColor' 
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span>Resetting...</span>
                </>
              ) : (
                'Reset Password'
              )}
            </button>

            {/* Back to Login */}
            <Link
              to='/'
              className='
                w-full h-12 
                flex items-center justify-center gap-2
                text-gray-600 dark:text-gray-400
                font-medium
                rounded-xl
                transition-all duration-200
                hover:bg-gray-100 dark:hover:bg-gray-800
              '
            >
              <HiOutlineArrowLeft className='w-5 h-5' />
              Back to Login
            </Link>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default ChangePassword;
